import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import axios from 'axios';
import { AlertTriangle, Clock, CheckCircle, XCircle, Bitcoin, Building2 } from 'lucide-react';
import { UsdtTrxIcon } from '@/components/UsdtTrxIcon';
import { ENABLE_CRYPTO_WITHDRAWALS } from '@/config/featureFlags';

const C = {
  bg: '#131413',
  accent: '#9EAFAC',
  accentDark: '#586060',
  border: 'rgba(158,175,172,0.15)',
  card: 'rgba(19,20,19,0.9)',
};

const STATUS_CFG = {
  pending:    { color: '#d97706', bg: 'rgba(217,119,6,0.08)',    border: 'rgba(217,119,6,0.25)',    Icon: Clock,         label: 'Pendiente'   },
  processing: { color: '#60a5fa', bg: 'rgba(96,165,250,0.08)',  border: 'rgba(96,165,250,0.25)',   Icon: Clock,         label: 'Procesando'  },
  completed:  { color: '#9EAFAC', bg: 'rgba(158,175,172,0.08)', border: 'rgba(158,175,172,0.2)',   Icon: CheckCircle,   label: 'Completado'  },
  rejected:   { color: '#f87171', bg: 'rgba(248,113,113,0.08)', border: 'rgba(248,113,113,0.2)',   Icon: XCircle,       label: 'Rechazado'   },
};

const BANK_OPTIONS = [
  'BANCO DE PICHINCHA',
  'PRODUBANCO',
  'BANCO DE GUAYAQUIL',
  'BANCO BOLIVARIANO',
  'BANCO DEL AUSTRO',
  'BANCO INTERNACIONAL',
  'OTRO',
];

const ACCOUNT_TYPES = ['Cuenta de ahorros', 'Cuenta corriente'];
const ID_TYPES = ['Cédula', 'RUC'];

// Obtener día de la semana en zona horaria de Ecuador (UTC-5)
function isFridayEcuador() {
  const nowUTC = new Date();
  // Restar 5 horas para obtener hora Ecuador
  const ecuadorOffset = -5 * 60; // minutos
  const utcMinutes = nowUTC.getTime() / 60000;
  const ecuadorDate = new Date((utcMinutes + ecuadorOffset) * 60000);
  return ecuadorDate.getUTCDay() === 5; // 5 = viernes
}

function getDayNameEcuador() {
  const days = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
  const nowUTC = new Date();
  const ecuadorOffset = -5 * 60;
  const utcMinutes = nowUTC.getTime() / 60000;
  const ecuadorDate = new Date((utcMinutes + ecuadorOffset) * 60000);
  return days[ecuadorDate.getUTCDay()];
}

// Estilo global para ocultar spinners en inputs numéricos
const hideSpinners = `
  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type=number] { -moz-appearance: textfield; }
  .method-btn { transition: all 0.22s ease; }
  .method-btn:hover { transform: translateY(-1px); }
`;

export default function WithdrawalPage() {
  const { user, getHeaders, API, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [method, setMethod] = useState(ENABLE_CRYPTO_WITHDRAWALS ? 'crypto' : 'bank_transfer');
  const [form, setForm] = useState({
    amount: '',
    wallet_address: '',
    bank_name: BANK_OPTIONS[0],
    bank_name_other: '',
    bank_account_type: ACCOUNT_TYPES[0],
    bank_account_number: '',
    bank_account_id_type: ID_TYPES[0],
    bank_account_id_number: '',
    bank_account_holder: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = window.matchMedia('(max-width: 768px)');
    const handleChange = (event) => setIsMobile(event.matches);
    setIsMobile(media.matches);
    if (media.addEventListener) {
      media.addEventListener('change', handleChange);
      return () => media.removeEventListener('change', handleChange);
    }
    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, []);

  const isFriday = isFridayEcuador();
  const dayName = getDayNameEcuador();
  const activeCycle = stats?.active_cycle;
  const paidPositions = Number(activeCycle?.paid_positions_count || 0);
  const cycleCapacity = Number(activeCycle?.capacity || 14);
  const withdrawalUnlockPositions = Number(stats?.withdrawal_unlock_positions || activeCycle?.withdrawal_unlock_positions || 7);
  const withdrawalUnlocked = paidPositions >= withdrawalUnlockPositions;
  const canWithdraw = withdrawalUnlocked && isFriday && user?.investment_status === 'active';

  const fetchData = useCallback(async () => {
    try {
      setLoadError('');
      const [statsRes, withdrawRes] = await Promise.all([
        axios.get(`${API}/user/stats`, { headers: getHeaders() }),
        axios.get(`${API}/withdrawals`, { headers: getHeaders() }),
      ]);
      setStats(statsRes.data);
      setWithdrawals(withdrawRes.data);
    } catch (err) {
      setLoadError('No se pudo cargar tu billetera. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!ENABLE_CRYPTO_WITHDRAWALS && method === 'crypto') {
      setMethod('bank_transfer');
    }
  }, [method]);

  const availableBalance = stats?.available_balance || 0;

  const setMax = () => setForm(f => ({ ...f, amount: availableBalance.toFixed(2) }));
  const setHalf = () => setForm(f => ({ ...f, amount: (availableBalance / 2).toFixed(2) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!canWithdraw) {
      setError(withdrawalUnlocked ? 'Los retiros solo se procesan los viernes (hora Ecuador).' : 'Tu retiro estará disponible cuando completes 7 posiciones de tu red.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        amount: parseFloat(form.amount),
        withdrawal_method: method,
      };
      if (method === 'crypto') {
        payload.wallet_address = form.wallet_address.trim();
      } else {
        payload.bank_name = form.bank_name === 'OTRO' ? form.bank_name_other : form.bank_name;
        payload.bank_account_type = form.bank_account_type;
        payload.bank_account_number = form.bank_account_number;
        // Combinar tipo de ID + número
        payload.bank_account_id_number = `${form.bank_account_id_type}: ${form.bank_account_id_number}`;
        payload.bank_account_holder = form.bank_account_holder;
      }

      await axios.post(`${API}/withdrawals`, payload, { headers: getHeaders() });
      setSuccess('¡Solicitud de retiro enviada! Se procesará este viernes.');
      setForm({
        amount: '', wallet_address: '',
        bank_name: BANK_OPTIONS[0], bank_name_other: '',
        bank_account_type: ACCOUNT_TYPES[0],
        bank_account_number: '', bank_account_id_type: ID_TYPES[0],
        bank_account_id_number: '', bank_account_holder: '',
      });
      fetchData();
      refreshUser();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al enviar la solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '40px', height: '40px', border: `2px solid ${C.accent}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  if (loadError) return (
    <div style={{ minHeight: '100vh', background: C.bg, color: 'white', fontFamily: 'Inter, sans-serif' }}>
      <Navbar />
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '100px 24px 64px' }}>
        <div style={{ maxWidth: '420px', width: '100%', textAlign: 'center', background: C.card, border: `1px solid ${C.border}`, borderRadius: '20px', padding: '32px 24px' }}>
          <XCircle size={32} color="#f87171" style={{ marginBottom: '14px' }} />
          <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '24px', color: 'white', marginBottom: '8px' }}>No se pudo cargar tu billetera</h2>
          <p style={{ color: C.accentDark, lineHeight: 1.5 }}>Intenta nuevamente.</p>
        </div>
      </div>
    </div>
  );

  const inputStyle = {
    width: '100%',
    background: 'rgba(158,175,172,0.04)',
    border: `1px solid ${C.border}`,
    borderRadius: '10px',
    padding: '12px 16px',
    color: 'white',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'Inter, sans-serif',
  };

  const selectStyle = {
    ...inputStyle,
    appearance: 'none',
    WebkitAppearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23586060' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 14px center',
    paddingRight: '36px',
    cursor: 'pointer',
    background: '#1a1c1a',
    border: `1px solid ${C.border}`,
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: 'white', fontFamily: 'Inter, sans-serif' }}>
      <style>{hideSpinners}</style>
      <Navbar />
      <div style={{ paddingTop: isMobile ? '88px' : '100px', paddingBottom: '64px', padding: isMobile ? '88px 16px 64px' : '100px 24px 64px' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <p style={{ color: C.accentDark, fontSize: '12px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '12px' }}>Tus Fondos</p>
            <h1 style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 900, fontSize: 'clamp(32px, 5vw, 48px)', color: 'white', marginBottom: '8px', letterSpacing: '-1px' }}>Solicitar Retiro</h1>
            <p style={{ color: C.accentDark }}>Retiros disponibles desde 7 posiciones completadas · Referencia: $300</p>
          </div>

          {/* Balance Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '20px', padding: '24px' }}>
              <div style={{ color: C.accentDark, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '10px' }}>Saldo Disponible</div>
              <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '32px', fontWeight: 900, color: C.accent }} data-testid="withdrawal-balance">${availableBalance.toFixed(2)}</div>
              <div style={{ color: C.accentDark, fontSize: '12px', marginTop: '6px' }}>USDT</div>
            </div>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '20px', padding: '24px' }}>
              <div style={{ color: C.accentDark, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '10px' }}>Ciclo de retiro</div>
              <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '32px', fontWeight: 900, color: withdrawalUnlocked ? C.accent : C.accentDark }} data-testid="withdrawal-cycle-progress">
                {paidPositions}<span style={{ fontSize: '18px', color: C.accentDark }}>/ {cycleCapacity}</span>
              </div>
              <div style={{ color: C.accentDark, fontSize: '12px', marginTop: '6px' }}>Retiro desde {withdrawalUnlockPositions}/14 · ciclo completo $600</div>
            </div>
          </div>

          {/* Alerts */}
          {!canWithdraw && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
              {user?.investment_status !== 'active' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: '12px', padding: '14px' }}>
                  <XCircle size={18} color="#f87171" />
                  <p style={{ color: '#f87171', fontSize: '14px', margin: 0 }}>Debes tener una inversión activa para retirar.</p>
                </div>
              )}
              {!withdrawalUnlocked && user?.investment_status === 'active' && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', background: 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.25)', borderRadius: '12px', padding: '14px' }}>
                  <AlertTriangle size={18} color="#d97706" />
                  <div>
                    <p style={{ color: '#f59e0b', fontWeight: 700, fontSize: '14px', margin: '0 0 3px' }}>Tu retiro estará disponible cuando completes 7 posiciones de tu red.</p>
                    <p style={{ color: 'rgba(245,158,11,0.7)', fontSize: '13px', margin: 0 }}>Comparte siempre tu link personal. Tus 2 invitados deben registrarse desde este enlace.</p>
                  </div>
                </div>
              )}
              {!isFriday && withdrawalUnlocked && user?.investment_status === 'active' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.2)', borderRadius: '12px', padding: '14px' }}>
                  <Clock size={18} color="#60a5fa" />
                  <p style={{ color: '#93c5fd', fontSize: '14px', margin: 0 }}>
                    Los retiros solo se procesan los <strong>viernes</strong> (hora Ecuador). Hoy es <strong>{dayName}</strong>. Vuelve el próximo viernes.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Form Card */}
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '20px', padding: '28px', marginBottom: '24px' }}>
            <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '18px', fontWeight: 700, color: 'white', marginBottom: '24px' }}>Nueva Solicitud de Retiro</h3>

            {/* Method Selector - mejorado */}
            <div style={{ display: 'grid', gridTemplateColumns: ENABLE_CRYPTO_WITHDRAWALS ? '1fr 1fr' : '1fr', gap: '10px', marginBottom: '20px' }}>
              {ENABLE_CRYPTO_WITHDRAWALS && <button
                type="button"
                className="method-btn"
                onClick={() => setMethod('crypto')}
                style={{
                  cursor: 'pointer',
                  textAlign: 'left',
                  background: method === 'crypto' ? 'rgba(158,175,172,0.12)' : 'rgba(158,175,172,0.03)',
                  border: `1.5px solid ${method === 'crypto' ? C.accent : C.border}`,
                  borderRadius: '12px',
                  padding: '14px 16px',
                  color: method === 'crypto' ? 'white' : C.accentDark,
                  boxShadow: method === 'crypto' ? `0 0 16px rgba(158,175,172,0.18)` : 'none',
                  outline: 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <Bitcoin size={15} color={method === 'crypto' ? C.accent : C.accentDark} />
                  <span style={{ fontWeight: 700, fontSize: '13px' }}>Criptomonedas</span>
                </div>
                <div style={{ fontSize: '11px', color: C.accentDark }}>USDT · Red TRC20</div>
              </button>}

              <button
                type="button"
                className="method-btn"
                onClick={() => setMethod('bank_transfer')}
                style={{
                  cursor: 'pointer',
                  textAlign: 'left',
                  background: method === 'bank_transfer' ? 'rgba(158,175,172,0.12)' : 'rgba(158,175,172,0.03)',
                  border: `1.5px solid ${method === 'bank_transfer' ? C.accent : C.border}`,
                  borderRadius: '12px',
                  padding: '14px 16px',
                  color: method === 'bank_transfer' ? 'white' : C.accentDark,
                  boxShadow: method === 'bank_transfer' ? `0 0 16px rgba(158,175,172,0.18)` : 'none',
                  outline: 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <Building2 size={15} color={method === 'bank_transfer' ? C.accent : C.accentDark} />
                  <span style={{ fontWeight: 700, fontSize: '13px' }}>Transferencia Bancaria</span>
                </div>
                <div style={{ fontSize: '11px', color: C.accentDark }}>Ecuador · Cuenta bancaria</div>
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

              {/* Amount con botones MAX y 50% */}
              <div>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    type="number"
                    value={form.amount}
                    onChange={e => setForm({ ...form, amount: e.target.value })}
                    placeholder={isMobile ? `Monto disponible: $${availableBalance.toFixed(2)}` : `Monto (Disponible: $${availableBalance.toFixed(2)})`}
                    max={availableBalance}
                    min={1}
                    step="0.01"
                    data-testid="withdrawal-amount"
                    required
                    style={{
                      ...inputStyle,
                      paddingRight: isMobile ? '16px' : '100px',
                      fontSize: isMobile ? '13px' : '14px',
                    }}
                  />
                  {!isMobile && (
                    <div style={{ position: 'absolute', right: '8px', display: 'flex', gap: '4px' }}>
                      <button
                        type="button"
                        onClick={setHalf}
                        style={{
                          padding: '5px 10px',
                          background: 'rgba(158,175,172,0.1)',
                          border: `1px solid ${C.border}`,
                          borderRadius: '6px',
                          color: C.accent,
                          fontSize: '11px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontFamily: 'Inter, sans-serif',
                        }}
                      >50%</button>
                      <button
                        type="button"
                        onClick={setMax}
                        style={{
                          padding: '5px 10px',
                          background: 'rgba(158,175,172,0.15)',
                          border: `1px solid rgba(158,175,172,0.3)`,
                          borderRadius: '6px',
                          color: 'white',
                          fontSize: '11px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontFamily: 'Inter, sans-serif',
                        }}
                      >MAX</button>
                    </div>
                  )}
                </div>
                {isMobile && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                    <button
                      type="button"
                      onClick={setHalf}
                      style={{
                        padding: '6px 12px',
                        background: 'rgba(158,175,172,0.1)',
                        border: `1px solid ${C.border}`,
                        borderRadius: '6px',
                        color: C.accent,
                        fontSize: '11px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >50%</button>
                    <button
                      type="button"
                      onClick={setMax}
                      style={{
                        padding: '6px 12px',
                        background: 'rgba(158,175,172,0.15)',
                        border: `1px solid rgba(158,175,172,0.3)`,
                        borderRadius: '6px',
                        color: 'white',
                        fontSize: '11px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >MAX</button>
                  </div>
                )}
              </div>

              {/* Campos según método */}
              {ENABLE_CRYPTO_WITHDRAWALS && method === 'crypto' ? (
                <>
                  <input
                    type="text"
                    value={form.wallet_address}
                    onChange={e => setForm({ ...form, wallet_address: e.target.value })}
                    placeholder="Dirección Wallet USDT · TRC20"
                    data-testid="withdrawal-wallet"
                    required
                    style={inputStyle}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(158,175,172,0.04)', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '10px 14px' }}>
                    <UsdtTrxIcon size="sm" />
                    <p style={{ color: C.accentDark, fontSize: '12px', margin: 0 }}>Solo red <strong style={{ color: C.accent }}>TRC20</strong> · La dirección comienza con 'T' y tiene 34 caracteres</p>
                  </div>
                </>
              ) : (
                <>
                  {/* Banco selector */}
                  <div>
                    <label style={{ color: C.accentDark, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px', display: 'block' }}>Banco</label>
                    <select
                      value={form.bank_name}
                      onChange={e => setForm({ ...form, bank_name: e.target.value })}
                      style={selectStyle}
                    >
                      {BANK_OPTIONS.map(opt => <option key={opt} value={opt} style={{ background: '#1a1c1a', color: 'white' }}>{opt}</option>)}
                    </select>
                  </div>

                  {form.bank_name === 'OTRO' && (
                    <input
                      type="text"
                      value={form.bank_name_other}
                      onChange={e => setForm({ ...form, bank_name_other: e.target.value })}
                      placeholder="Nombre del banco"
                      required
                      style={inputStyle}
                    />
                  )}

                  {/* Tipo de cuenta selector */}
                  <div>
                    <label style={{ color: C.accentDark, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px', display: 'block' }}>Tipo de cuenta</label>
                    <select
                      value={form.bank_account_type}
                      onChange={e => setForm({ ...form, bank_account_type: e.target.value })}
                      style={selectStyle}
                    >
                      {ACCOUNT_TYPES.map(t => <option key={t} value={t} style={{ background: '#1a1c1a', color: 'white' }}>{t}</option>)}
                    </select>
                  </div>

                  {/* Identificación dividida */}
                  <div>
                    <label style={{ color: C.accentDark, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px', display: 'block' }}>Identificación</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '8px' }}>
                      <select
                        value={form.bank_account_id_type}
                        onChange={e => setForm({ ...form, bank_account_id_type: e.target.value })}
                        style={{ ...selectStyle, width: '100%' }}
                      >
                        {ID_TYPES.map(t => <option key={t} value={t} style={{ background: '#1a1c1a', color: 'white' }}>{t}</option>)}
                      </select>
                      <input
                        type="text"
                        value={form.bank_account_id_number}
                        onChange={e => setForm({ ...form, bank_account_id_number: e.target.value })}
                        placeholder="Número de identificación"
                        required
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  {/* Número de cuenta */}
                  <div>
                    <label style={{ color: C.accentDark, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px', display: 'block' }}>Número de cuenta</label>
                    <input
                      type="text"
                      value={form.bank_account_number}
                      onChange={e => setForm({ ...form, bank_account_number: e.target.value })}
                      placeholder="Número de cuenta bancaria"
                      required
                      style={inputStyle}
                    />
                  </div>

                  {/* Nombre del titular */}
                  <div>
                    <label style={{ color: C.accentDark, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px', display: 'block' }}>Nombre del titular</label>
                    <input
                      type="text"
                      value={form.bank_account_holder}
                      onChange={e => setForm({ ...form, bank_account_holder: e.target.value })}
                      placeholder="Nombre completo del titular"
                      required
                      style={inputStyle}
                    />
                  </div>
                </>
              )}

              {error && <div data-testid="withdrawal-error" style={{ color: '#fc8181', fontSize: '14px' }}>{error}</div>}
              {success && <div data-testid="withdrawal-success" style={{ color: C.accent, fontSize: '14px' }}>{success}</div>}

              <button
                type="submit"
                disabled={!canWithdraw || submitting}
                data-testid="withdrawal-submit"
                className="btn-glow"
                style={{
                  width: '100%',
                  padding: '16px',
                  background: !canWithdraw || submitting ? C.accentDark : C.accent,
                  border: 'none',
                  borderRadius: '12px',
                  color: '#131413',
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 800,
                  fontSize: '16px',
                  cursor: !canWithdraw || submitting ? 'not-allowed' : 'pointer',
                  opacity: !canWithdraw || submitting ? 0.5 : 1,
                  transition: 'all 0.2s',
                }}
              >
                {submitting ? 'Enviando...' : 'Solicitar Retiro'}
              </button>
            </form>
          </div>

          {/* Historial de Retiros */}
          {withdrawals.length > 0 && (
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '20px', padding: '28px' }}>
              <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '18px', fontWeight: 700, color: 'white', marginBottom: '20px' }}>Historial de Retiros</h3>
              <div data-testid="withdrawal-history" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {withdrawals.map((w, idx) => {
                  const cfg = STATUS_CFG[w.status] || STATUS_CFG.pending;
                  const StatusIcon = cfg.Icon;
                  return (
                    <div key={w.id} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 0', borderBottom: idx < withdrawals.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                      <div style={{ padding: '8px', background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: '10px' }}>
                        <StatusIcon size={16} color={cfg.color} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: 'white', fontWeight: 600, fontSize: '15px' }}>${parseFloat(w.amount).toFixed(2)} USDT</div>
                        <div style={{ color: C.accentDark, fontSize: '12px' }}>
                          {w.withdrawal_method === 'bank_transfer'
                            ? `${w.bank_name || ''} · ${w.bank_account_number || ''}`
                            : `${w.wallet_address?.slice(0, 16)}...`}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: cfg.color, fontSize: '12px', fontWeight: 700 }}>{cfg.label}</div>
                        <div style={{ color: C.accentDark, fontSize: '12px' }}>{new Date(w.requested_at).toLocaleDateString('es-ES')}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
